import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { CommandItem } from '@/components/ui/command';
import { Label } from '@/components/ui/label';

import { getFilterOption } from '../store/filter-labels-store';
import { TableFilterOption } from '../types/types';

/**
 * Minimum number of options a filter must have before the pinned "Selected"
 * section is worth showing. Below these thresholds the whole list is short
 * enough to scan at a glance, so the section only adds noise.
 *
 * The threshold is lower for searchable filters: a search field implies a
 * longer list, so the section becomes useful sooner.
 */
export const SELECTED_SECTION_MIN_OPTIONS_WITH_SEARCH = 8;
export const SELECTED_SECTION_MIN_OPTIONS_WITHOUT_SEARCH = 15;

/**
 * Whether the pinned "Selected" section should be shown for a filter.
 *
 * @param hasSearch    Whether the filter renders a search field.
 * @param totalOptions Total number of options. For paginated filters where not
 *   every option is loaded, pass a value at/above the threshold (see callers).
 */
export function shouldShowSelectedSection(hasSearch: boolean, totalOptions: number): boolean {
  const threshold = hasSearch
    ? SELECTED_SECTION_MIN_OPTIONS_WITH_SEARCH
    : SELECTED_SECTION_MIN_OPTIONS_WITHOUT_SEARCH;
  return totalOptions > threshold;
}

interface SelectedOptionsSectionProps {
  /** Filter config name — used to resolve labels from the persisted labels cache. */
  filterName: string;
  /** Currently selected values (order preserved as given). */
  selectedValues: string[];
  /**
   * Options currently loaded in the list. Used to resolve a selected value's full
   * metadata (label, avatar, color) when it happens to be on the loaded page.
   */
  loadedOptions: TableFilterOption[];
  /** Static pinned options (e.g. "No department") whose labels aren't in the API list. */
  topOptions?: TableFilterOption[];
  /** Toggles a value's selection (removes it, since these are all selected). */
  onToggle: (value: string) => void;
  /** Renders a single option's label (avatar/status/etc.). */
  renderLabel: (option: TableFilterOption) => ReactNode;
}

/**
 * Resolves the display metadata for a selected value.
 *
 * A selected value may not be present on the currently loaded page (the list is
 * paginated + searchable), so resolution falls back through several sources:
 * loaded options → pinned top options → persisted labels cache. The cache is
 * populated whenever options are loaded, so selections survive pagination,
 * searching, and page refreshes. As a last resort the raw value is shown.
 */
function resolveSelectedOption(
  value: string,
  filterName: string,
  loadedOptions: TableFilterOption[],
  topOptions: TableFilterOption[]
): TableFilterOption {
  const fromLoaded = loadedOptions.find(o => o.value === value);
  if (fromLoaded) return fromLoaded;

  const fromTop = topOptions.find(o => o.value === value);
  if (fromTop) return fromTop;

  const cached = getFilterOption(filterName, value);
  if (cached) {
    return { value, label: cached.label, color: cached.color, avatar: cached.avatar };
  }

  return { value, label: value };
}

/** Centered "Selected" divider — label in the middle with a line on each side. */
function SelectedHeading({ label }: { label: string }) {
  return (
    <div className="px-2 py-1.5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" />
        <span className="shrink-0">{label}</span>
        <div className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}

/**
 * The "Selected" section pinned above the option list in a multi-select filter.
 *
 * Layout: a centered "Selected" divider, then every currently-selected option (so
 * the user always sees their picks first — even ones that live on a later page),
 * then a plain divider separating this section from the full option list below.
 * The full list below still shows these same options (selected + unselected), so
 * toggling a value off in either place keeps both in sync via the shared `onToggle`.
 *
 * Renders nothing when there is no selection. Pinned top options (e.g. "No value")
 * are excluded so they keep their fixed slot in the main list.
 */
export function SelectedOptionsSection({
  filterName,
  selectedValues,
  loadedOptions,
  topOptions = [],
  onToggle,
  renderLabel,
}: SelectedOptionsSectionProps) {
  const { t } = useTranslation('common');

  // Pinned top options keep their own fixed slot in the main list — don't surface
  // them here even when selected.
  const topValues = new Set(topOptions.map(o => o.value));
  const sectionValues = selectedValues.filter(v => !topValues.has(v));

  if (sectionValues.length === 0) return null;

  return (
    <>
      <SelectedHeading label={t('tableFilters.selectedSection')} />
      {sectionValues.map(value => {
        const option = resolveSelectedOption(value, filterName, loadedOptions, topOptions);
        return (
          <CommandItem
            key={`selected-${value}`}
            // Keep the label in the cmdk `value` so built-in search (used by the
            // static submenu) still matches a selected option by its name.
            value={`selected-${option.label}-${value}`}
            onSelect={() => onToggle(value)}
            className="flex min-w-0 cursor-pointer items-center gap-2 overflow-hidden"
          >
            <Checkbox checked className="shrink-0" />
            <Label className="flex min-w-0 flex-1 cursor-pointer overflow-hidden">
              {renderLabel(option)}
            </Label>
          </CommandItem>
        );
      })}
      {/* Plain divider (no text) between the selected section and the full list. */}
      <div className="my-1 h-px bg-border" />
    </>
  );
}
